import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { DataSourceItem } from './datasource-constants';
import {
  REPO_LANGUAGE_OPTIONS,
  REPO_FRAMEWORK_OPTIONS,
  REPO_BUILD_TOOL_OPTIONS,
  REPO_CRITICALITY_OPTIONS,
  REPO_TYPE_OPTIONS,
} from './datasource-constants';

export default function PlatformTable({
  platformName,
  items,
  onDelete,
  onUpdateRepoMeta,
}: {
  platformName: string;
  items: DataSourceItem[];
  onDelete: (item: DataSourceItem) => void;
  onUpdateRepoMeta?: (item: DataSourceItem, field: string, value: any) => void;
}) {
  const theme = useTheme();
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const toggleRow = (url: string) => {
    setExpandedRow(prev => (prev === url ? null : url));
  };

  return (
    <Card
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}
    >
      <CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          {platformName}{' '}
          <Chip label={items.length} size="small" sx={{ ml: 1 }} />
        </Typography>
        {items.length > 0 ? (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider' }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  }}
                >
                  {onUpdateRepoMeta && <TableCell sx={{ width: 32 }} />}
                  <TableCell sx={{ fontWeight: 700 }}>URL</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Branch</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Criticality</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => {
                  const isExpanded = expandedRow === item.url;
                  return (
                    <>
                      <TableRow key={`${item.url}-${index}`} hover>
                        {onUpdateRepoMeta && (
                          <TableCell sx={{ p: 0, pl: 0.5, width: 32 }}>
                            <IconButton size="small" onClick={() => toggleRow(item.url)}>
                              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </IconButton>
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                          >
                            {item.url}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.branch || 'default'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: 12 }}>
                            {item.criticality || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: 12 }}>
                            {item.type || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDelete(item)}
                          >
                            <Trash2 size={18} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {onUpdateRepoMeta && (
                        <TableRow key={`${item.url}-expand-${index}`}>
                          <TableCell colSpan={6} sx={{ py: 0, border: isExpanded ? undefined : 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 1.5, px: 1 }}>
                                <Stack spacing={1.5}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                      <InputLabel>Criticality</InputLabel>
                                      <Select
                                        value={item.criticality || ''}
                                        label="Criticality"
                                        onChange={(e) => onUpdateRepoMeta(item, 'criticality', e.target.value)}
                                      >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {REPO_CRITICALITY_OPTIONS.map(c => (
                                          <MenuItem key={c} value={c}>{c}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                      <InputLabel>Type</InputLabel>
                                      <Select
                                        value={item.type || ''}
                                        label="Type"
                                        onChange={(e) => onUpdateRepoMeta(item, 'type', e.target.value)}
                                      >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {REPO_TYPE_OPTIONS.map(t => (
                                          <MenuItem key={t} value={t}>{t}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Stack>
                                  <Autocomplete
                                    multiple freeSolo size="small"
                                    options={REPO_LANGUAGE_OPTIONS}
                                    value={item.languages || []}
                                    onChange={(_, v) => onUpdateRepoMeta(item, 'languages', v)}
                                    renderInput={(params) => <TextField {...params} label="Languages" />}
                                  />
                                  <Autocomplete
                                    multiple freeSolo size="small"
                                    options={REPO_FRAMEWORK_OPTIONS}
                                    value={item.frameworks || []}
                                    onChange={(_, v) => onUpdateRepoMeta(item, 'frameworks', v)}
                                    renderInput={(params) => <TextField {...params} label="Frameworks" />}
                                  />
                                  <Autocomplete
                                    multiple freeSolo size="small"
                                    options={REPO_BUILD_TOOL_OPTIONS}
                                    value={item.buildTools || []}
                                    onChange={(_, v) => onUpdateRepoMeta(item, 'buildTools', v)}
                                    renderInput={(params) => <TextField {...params} label="Build Tools" />}
                                  />
                                </Stack>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No {platformName.toLowerCase()} sources configured
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
